import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { BY_SYMBOL, elementColor } from './elements';
import { parseFormula, molarMass } from './formula';
import {
  VizCard,
  NowYouTry,
  VIZ_BTN_GHOST,
  DARK_INK,
  usePrefersReducedMotion,
  cueIsActive,
  type VizCue,
} from './vizShared';

// FormulaBreakdown — parse a formula ("H2O", "CO2", "C6H12O6") into grouped atom
// particles + a count table, with an "assembly" animation (atoms fade/scale in
// one group at a time). config: { formula, title? }. Resilient: invalid formula
// shows an empty-state message rather than crashing.

export interface FormulaBreakdownConfig {
  title?: string;
  formula?: string;
}

interface Props {
  config?: FormulaBreakdownConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

export default function FormulaBreakdown({ config = {}, onComplete, cue }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const formula = config.formula ?? 'H2O';
  const atoms = useMemo(() => parseFormula(formula), [formula]);
  const total = useMemo(() => atoms.reduce((s, a) => s + a.count, 0), [atoms]);
  const mass = useMemo(() => molarMass(formula), [formula]);

  // Reveal atoms progressively for the assembly animation. When motion is
  // reduced or a cue is driving, reveal everything immediately.
  const autoReveal = !reducedMotion && !cueOn;
  const [revealed, setRevealed] = useState(autoReveal ? 0 : total);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);

  useEffect(() => {
    if (!autoReveal) {
      setRevealed(total);
      return;
    }
    setRevealed(0);
    if (total === 0) return;
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      setRevealed(n);
      if (n >= total) {
        window.clearInterval(id);
        setShowTry(true);
      }
    }, 180);
    return () => window.clearInterval(id);
    // Arm once per formula on mount; autoReveal is read at arm time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formula, total]);

  // --- Cue: progress/step drive revealed count; play re-triggers assembly. ----
  useEffect(() => {
    if (!cue) return;
    setShowTry(false);
    if (typeof cue.progress === 'number') {
      const frac = Math.max(0, Math.min(1, cue.progress));
      setRevealed(Math.round(frac * total));
    } else if (typeof cue.step === 'number') {
      setRevealed(Math.max(0, Math.min(total, Math.round(cue.step))));
    } else if (cue.play) {
      setRevealed(0);
      window.setTimeout(() => setRevealed(total), 50);
    }
  }, [cue, total]);

  const replay = () => {
    setShowTry(false);
    setRevealed(0);
    window.setTimeout(() => setRevealed(total), 50);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'chemical_formula', formula });
    }
  };

  // Mark complete once the assembly finishes the first time.
  useEffect(() => {
    if (revealed >= total && total > 0 && !interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'chemical_formula', formula });
    }
  }, [revealed, total, formula, onComplete]);

  if (total === 0) {
    return (
      <VizCard title={config.title ?? 'Formula breakdown'} reducedMotion={reducedMotion}>
        <div className="rounded-md border border-ws-border bg-ws-surface-2 p-4 text-sm text-ws-ink-3">
          Could not parse formula <code className="rounded bg-ws-surface px-1 text-xs">{formula}</code>.
        </div>
      </VizCard>
    );
  }

  // Flat list of atom particles, grouped by element, for the visual.
  let idx = 0;
  return (
    <VizCard
      title={
        <span>
          {config.title ?? 'Formula breakdown'} ·{' '}
          <span className="font-mono">{prettyFormula(formula)}</span>
        </span>
      }
      reducedMotion={reducedMotion}
      actions={
        <button type="button" onClick={replay} className={VIZ_BTN_GHOST}>
          <RotateCcw size={12} /> Replay
        </button>
      }
    >
      {/* Atom particles grouped by element */}
      <div className="relative flex flex-wrap items-start gap-4 rounded-md border border-ws-border bg-ws-surface-2 p-4">
        <NowYouTry show={showTry} />
        {atoms.map(({ symbol, count }) => (
          <div key={symbol} className="flex flex-col items-center gap-1.5">
            <div className="flex flex-wrap justify-center gap-1" style={{ maxWidth: 132 }}>
              {Array.from({ length: count }).map((_, i) => {
                const myIndex = idx++;
                const shown = myIndex < revealed;
                return (
                  <span
                    key={i}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold transition-all duration-300 ease-out"
                    style={{
                      color: DARK_INK,
                      backgroundColor: elementColor(symbol),
                      border: '1px solid rgba(0,0,0,0.25)',
                      opacity: shown ? 1 : 0,
                      transform: shown ? 'scale(1)' : 'scale(0.3)',
                      // subtle stagger within a group
                      transitionDelay: shown && !reducedMotion ? `${(myIndex % 6) * 20}ms` : '0ms',
                    }}
                  >
                    {symbol}
                  </span>
                );
              })}
            </div>
            <span className="text-[11px] text-ws-ink-3">
              {BY_SYMBOL[symbol]?.name ?? symbol} ×{count}
            </span>
          </div>
        ))}
      </div>

      {/* Count table */}
      <table className="mt-3 w-full text-left text-xs">
        <thead>
          <tr className="border-b border-ws-border text-ws-ink-3">
            <th className="py-1 font-medium">Element</th>
            <th className="py-1 font-medium">Symbol</th>
            <th className="py-1 font-medium">Atoms</th>
            <th className="py-1 font-medium">Mass contrib.</th>
          </tr>
        </thead>
        <tbody>
          {atoms.map(({ symbol, count }) => {
            const e = BY_SYMBOL[symbol];
            return (
              <tr key={symbol} className="border-b border-ws-border text-ws-ink-2">
                <td className="py-1">{e?.name ?? '—'}</td>
                <td className="py-1 font-mono">{symbol}</td>
                <td className="py-1">{count}</td>
                <td className="py-1">{e ? (e.mass * count).toFixed(2) : '—'}</td>
              </tr>
            );
          })}
          <tr className="font-semibold text-ws-ink">
            <td className="py-1" colSpan={2}>Total ({total} atoms)</td>
            <td className="py-1">{total}</td>
            <td className="py-1">{mass.toFixed(2)} g/mol</td>
          </tr>
        </tbody>
      </table>
    </VizCard>
  );
}

// Render subscripts as small digits (visual only).
function prettyFormula(formula: string): string {
  return formula.replace(/(\d+)/g, (m) =>
    m.split('').map((d) => '₀₁₂₃₄₅₆₇₈₉'[Number(d)]).join(''),
  );
}
